import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Lock, Plus, Edit2, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';

interface UnlockForm {
  requiredDirectReferrals: number;
  maxUnlockedLevel: number;
  isActive: boolean;
}

const INITIAL_FORM: UnlockForm = {
  requiredDirectReferrals: 0,
  maxUnlockedLevel: 10,
  isActive: true,
};

function RuleFormModal({ isOpen, onClose, rule }: { isOpen: boolean; onClose: () => void; rule?: any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<UnlockForm>(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const isEdit = !!rule;

  useEffect(() => {
    if (isOpen) {
      if (rule) {
        setForm({
          requiredDirectReferrals: rule.requiredDirectReferrals || 0,
          maxUnlockedLevel: rule.maxUnlockedLevel || 10,
          isActive: rule.isActive !== false,
        });
      } else {
        setForm(INITIAL_FORM);
      }
      setError('');
      setSuccess(false);
    }
  }, [isOpen, rule]);

  const mutation = useMutation({
    mutationFn: (data: UnlockForm) => isEdit ? api.patch(`/admin/level-unlock-rules/${rule.id}`, data) : api.post('/admin/level-unlock-rules', data),
    onSuccess: () => { setSuccess(true); qc.invalidateQueries({ queryKey: ['level-unlock-rules'] }); },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to save rule'),
  });

  const set = (key: keyof UnlockForm, value: any) => setForm(prev => ({ ...prev, [key]: value }));
  const handleSubmit = () => {
    setError('');
    if (form.requiredDirectReferrals <= 0) { setError('Required direct referrals must be > 0'); return; }
    if (form.maxUnlockedLevel <= 0) { setError('Max unlocked level must be > 0'); return; }
    if (form.maxUnlockedLevel > 30) { setError('Max unlocked level cannot exceed 30'); return; }
    mutation.mutate(form);
  };
  const handleClose = () => { onClose(); };
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Edit Unlock Rule' : 'Create Unlock Rule'} maxWidth="max-w-md">
      {success ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-400">{isEdit ? 'Rule Updated' : 'Rule Created'}</p>
              <p className="text-xs text-slate-400 mt-0.5">Level unlock rule saved successfully</p>
            </div>
          </div>
          <Button onClick={handleClose} className="w-full">Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Required Active Direct Referrals</label>
            <input type="number" value={form.requiredDirectReferrals || ''} onChange={e => set('requiredDirectReferrals', parseInt(e.target.value) || 0)} placeholder="10" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            <p className="text-xs text-slate-500 mt-1">Minimum active direct referrals needed to unlock levels</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Maximum Unlocked Level</label>
            <input type="number" value={form.maxUnlockedLevel || ''} onChange={e => set('maxUnlockedLevel', parseInt(e.target.value) || 0)} placeholder="10" min="1" max="30" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            <p className="text-xs text-slate-500 mt-1">Levels 1 through this value will be unlocked (max 30)</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-slate-400">Active</label>
            <button type="button" onClick={() => set('isActive', !form.isActive)} className={`relative w-10 h-5 rounded-full transition-colors ${form.isActive ? 'bg-primary-600' : 'bg-white/10'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-5' : ''}`} />
            </button>
            <span className="text-xs text-slate-400">{form.isActive ? 'Active' : 'Inactive'}</span>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} isLoading={mutation.isPending} className="flex-1">{isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function AdminLevelUnlockPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  const { data: rules, isLoading } = useQuery({
    queryKey: ['level-unlock-rules'],
    queryFn: () => api.get('/admin/level-unlock-rules').then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/level-unlock-rules/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['level-unlock-rules'] }); setDeleteConfirm(null); },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Level Unlock Rules</h1>
          <p className="text-slate-400 mt-1">Configure how many direct referrals unlock each level tier</p>
        </div>
        <Button onClick={() => { setEditingRule(null); setModalOpen(true); }}><Plus className="w-4 h-4" />Add Rule</Button>
      </div>

      <div className="p-4 rounded-xl bg-primary-600/5 border border-primary-500/10">
        <p className="text-sm text-slate-400">
          <span className="text-white font-medium">How it works:</span> When a member has the required number of active direct referrals, levels up to the configured maximum are unlocked for Level Income. Levels beyond the maximum remain locked and receive no income.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-white/[0.02] animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {rules?.map((rule: any) => (
            <div key={rule.id} className={`d-card-hover p-4 flex items-center justify-between ${!rule.isActive ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary-400" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-white">{rule.requiredDirectReferrals} Active Directs</p>
                    <span className="text-slate-600">→</span>
                    <p className="text-sm font-semibold text-primary-400">Unlock Level 1–{rule.maxUnlockedLevel}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">When a member has {rule.requiredDirectReferrals} or more active direct referrals, levels 1 through {rule.maxUnlockedLevel} are unlocked</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge status={rule.isActive ? 'ACTIVE' : 'INACTIVE'} />
                <button onClick={() => { setEditingRule(rule); setModalOpen(true); }} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => setDeleteConfirm(rule)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rules?.length === 0 && (
        <div className="text-center py-12">
          <Lock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No level unlock rules configured</p>
          <Button onClick={() => { setEditingRule(null); setModalOpen(true); }} className="mt-4">Create First Rule</Button>
        </div>
      )}

      <RuleFormModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingRule(null); }} rule={editingRule} />

      {deleteConfirm && (
        <Modal isOpen={true} onClose={() => setDeleteConfirm(null)} title="Delete Rule">
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Are you sure you want to delete the rule <span className="text-white font-medium">{deleteConfirm.requiredDirectReferrals} directs → Level 1–{deleteConfirm.maxUnlockedLevel}</span>?</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button>
              <Button variant="danger" onClick={() => deleteMutation.mutate(deleteConfirm.id)} isLoading={deleteMutation.isPending} className="flex-1">Delete</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
